// Dev-only impersonation override, so one AAD sign-in can exercise the peer-vs-self
// approval rules and the role-gated UI. Read in two places: cspApi.js stamps the
// X-Dev-Override-* headers, and useCSPLicenseEffectiveRoles feeds the UI gates.
//
// Inert outside development: the hook returns real roles in production builds and
// the controller half is behind #if DEBUG.

import { useEffect, useState } from 'react'

export const DEV_OVERRIDE_UPN_KEY = 'cspLicenses.devOverrideUpn'
export const DEV_OVERRIDE_ROLES_KEY = 'cspLicenses.devOverrideRoles'

const isDev = process.env.NODE_ENV === 'development'

// Cross-tab + same-tab change broadcaster. The browser only fires the
// native `storage` event on OTHER tabs by default; we dispatch a custom
// event on writes so the panel + role hook within the same tab stay in
// sync with each other.
const CHANGE_EVENT = 'csp-licenses-dev-override-changed'

export const readDevOverride = () => {
  if (!isDev || typeof window === 'undefined') {
    return { upn: null, roles: null, active: false }
  }
  try {
    const upn = window.localStorage.getItem(DEV_OVERRIDE_UPN_KEY) || null
    const rolesRaw = window.localStorage.getItem(DEV_OVERRIDE_ROLES_KEY) || null
    const roles = rolesRaw
      ? rolesRaw.split(',').map((r) => r.trim()).filter(Boolean)
      : null
    return { upn, roles, active: !!(upn || (roles && roles.length > 0)) }
  } catch (_) {
    return { upn: null, roles: null, active: false }
  }
}

export const writeDevOverride = ({ upn, roles }) => {
  if (!isDev || typeof window === 'undefined') return
  try {
    if (upn) window.localStorage.setItem(DEV_OVERRIDE_UPN_KEY, upn)
    else window.localStorage.removeItem(DEV_OVERRIDE_UPN_KEY)
    if (roles && roles.length > 0) window.localStorage.setItem(DEV_OVERRIDE_ROLES_KEY, roles.join(','))
    else window.localStorage.removeItem(DEV_OVERRIDE_ROLES_KEY)
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch (_) {
    // Same private-browsing fallback as cspApi.js — silent no-op.
  }
}

export const clearDevOverride = () => writeDevOverride({ upn: null, roles: null })

// Subscribe to override changes — both same-tab (custom event) and
// cross-tab (native storage event). Used by the dev panel + the
// effective-roles hook to re-render on flip.
const useDevOverride = () => {
  const [state, setState] = useState(() => readDevOverride())
  useEffect(() => {
    if (!isDev || typeof window === 'undefined') return undefined
    const refresh = () => setState(readDevOverride())
    window.addEventListener(CHANGE_EVENT, refresh)
    window.addEventListener('storage', refresh)
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh)
      window.removeEventListener('storage', refresh)
    }
  }, [])
  return state
}

// Use this everywhere a CSP Licences component is deciding what to
// show based on the caller's roles. The override (when set) REPLACES
// the AAD roles entirely — same shape the backend honours, so the
// UI and the API auth stay in sync.
export const useCSPLicenseEffectiveRoles = (realRoles) => {
  const override = useDevOverride()
  if (override.active && override.roles && override.roles.length > 0) {
    return override.roles
  }
  return realRoles || []
}

export const useCSPLicenseEffectiveUpn = (realUpn) => {
  const override = useDevOverride()
  if (override.active && override.upn) return override.upn
  return realUpn || ''
}

export const useDevOverrideState = useDevOverride
